// A venue's printers and print jobs (F-OPS-08 Printer management; spec/13 §11), as TanStack Query hooks over the
// generated client — `packages/contracts/src/routes/printers.ts`:
//
//   GET    …/venues/{v}/printers              the venue's printers, with their status   settings:hardware
//   POST   …/venues/{v}/printers              add one                                   settings:hardware
//   PATCH  …/venues/{v}/printers/{p}          edit one (and rotate a cloud key)         settings:hardware
//   DELETE …/venues/{v}/printers/{p}          archive one                               settings:hardware
//   POST   …/venues/{v}/printers/{p}/test     a test page through it                    settings:hardware
//   GET    …/venues/{v}/print-jobs            the recent print jobs, newest first       settings:hardware
//
// The Printers and hardware settings group manages them; the kitchen board reads the list only to name the
// printer a ticket went to, and only when the member may read it.

import type { CreatePrinterRequest, Printer, PrintJob, UpdatePrinterRequest } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const SETTINGS_HARDWARE = 'settings:hardware';

/** How many recent jobs the settings group lists. */
const RECENT_JOBS = 20;

export const printerKeys = {
  list: (organizationId: string, venueId: string) => ['printers', organizationId, venueId, 'list'] as const,
  jobs: (organizationId: string, venueId: string) => ['printers', organizationId, venueId, 'jobs'] as const,
};

/** The venue's printers, by name. */
export function usePrinters(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery<readonly Printer[]>({
    queryKey: printerKeys.list(organizationId, venueId),
    enabled,
    queryFn: async () => (await api.listPrinters({ path: { organizationId, venueId } })).printers,
  });
}

/** The venue's recent print jobs, newest first — so a failure is visible rather than silent. */
export function usePrintJobs(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery<readonly PrintJob[]>({
    queryKey: printerKeys.jobs(organizationId, venueId),
    enabled,
    queryFn: async () => (await api.listPrintJobs({ path: { organizationId, venueId }, query: { limit: RECENT_JOBS } })).jobs,
  });
}

function useInvalidatePrinters(organizationId: string, venueId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: printerKeys.list(organizationId, venueId) });
    void queryClient.invalidateQueries({ queryKey: printerKeys.jobs(organizationId, venueId) });
  };
}

export function useCreatePrinter(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidatePrinters(organizationId, venueId);
  return useMutation({
    mutationFn: (body: CreatePrinterRequest) => api.createPrinter({ path: { organizationId, venueId }, body }),
    onSuccess: invalidate,
  });
}

export function useUpdatePrinter(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidatePrinters(organizationId, venueId);
  return useMutation({
    mutationFn: ({ printerId, body }: { readonly printerId: string; readonly body: UpdatePrinterRequest }) =>
      api.updatePrinter({ path: { organizationId, venueId, printerId }, body }),
    onSuccess: invalidate,
  });
}

export function useArchivePrinter(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidatePrinters(organizationId, venueId);
  return useMutation({
    mutationFn: (printerId: string) => api.archivePrinter({ path: { organizationId, venueId, printerId } }),
    onSuccess: invalidate,
  });
}

export function useTestPrinter(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useInvalidatePrinters(organizationId, venueId);
  return useMutation<PrintJob, unknown, string>({
    mutationFn: (printerId) => api.testPrinter({ path: { organizationId, venueId, printerId } }),
    onSuccess: invalidate,
  });
}

/**
 * A printer's name by id, when the member may read the venue's printers (`settings:hardware`); otherwise `null`,
 * and the caller says "the kitchen printer" instead.
 */
export function usePrinterName(organizationId: string, venueId: string, enabled: boolean): (printerId: string | null) => string | null {
  const printers = usePrinters(organizationId, venueId, enabled);
  return (printerId) => (printerId === null ? null : (printers.data?.find((printer) => printer.id === printerId)?.name ?? null));
}
